import { useCallback, useMemo } from 'react';
import { useProject } from './useProject';
import type { SavePhaseOptions } from '../providers/ProjectProvider';
import { IPhase } from '../types';

export const useProjectPhase = (phaseId?: string) => {
  const {
    phaseModels,
    updatePhase,
    setActivePhase,
    loading,
    savePhase,
    getPhaseById,
    phases,
  } = useProject();

  const resolvedPhaseId: string | null = useMemo(() => phaseId || null, [phaseId]);

  const phase = useMemo<IPhase | null>(
    () => (resolvedPhaseId ? getPhaseById(resolvedPhaseId) : null),
    [getPhaseById, phases, resolvedPhaseId]
  );

  const setData = useCallback(
    (data: unknown) => {
      if (!resolvedPhaseId) return;
      updatePhase(resolvedPhaseId, { data });
    },
    [resolvedPhaseId, updatePhase]
  );

  const savePhaseById = useCallback(
    async (patch?: Partial<IPhase>, options?: SavePhaseOptions) => {
      if (!resolvedPhaseId) return;
      await savePhase(resolvedPhaseId, patch, options);
    },
    [resolvedPhaseId, savePhase]
  );

  const activatePhase = useCallback(() => {
    if (!resolvedPhaseId) return;
    setActivePhase(resolvedPhaseId);
  }, [resolvedPhaseId, setActivePhase]);

  return {
    phase,
    model: resolvedPhaseId ? phaseModels[resolvedPhaseId] : undefined,
    setData,
    savePhase: savePhaseById,
    activatePhase,
    phaseId: resolvedPhaseId,
    loading,
  };
};
